import { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Alert, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
} from '@mui/material';
import StandardCard from '../ui/StandardCard';
import AppSnackbar from '../ui/AppSnackbar';
import useSnackbar from '../../hooks/useSnackbar';
import billingService from '../../services/billingService';
import PixQrCheckout from './PixQrCheckout';

function formatBRL(cents) {
  return (Number(cents || 0) / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatValidity(days) {
  if (!days) return 'Sem validade';
  if (days % 30 === 0) {
    const months = days / 30;
    return `Válido por ${months} ${months === 1 ? 'mês' : 'meses'}`;
  }
  return `Válido por ${days} dias`;
}

/**
 * ProductCatalog — vitrine reutilizável de pacotes (R2), parametrizada por `meter`
 * (ex.: 'whatsapp_messages'). Compra via PIX embeddado (D-1). Coleta o CPF no 1º uso, guiada
 * pelo backend: tenta sem `document`; se a API responde 400 (CPF obrigatório), abre o modal e
 * refaz com o CPF (a checagem de CPF roda ANTES de criar a intenção → sem order órfã).
 *
 * @param {{ meter: string }} props
 */
export default function ProductCatalog({ meter }) {
  const { snackbar, showError, hideSnackbar } = useSnackbar();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [buyingId, setBuyingId] = useState(null);
  const [checkoutOrder, setCheckoutOrder] = useState(null);

  // Modal de CPF (1º uso)
  const [cpfFor, setCpfFor] = useState(null);   // produto aguardando CPF
  const [cpf, setCpf] = useState('');
  const [cpfSubmitting, setCpfSubmitting] = useState(false);
  const [cpfError, setCpfError] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    billingService.listProducts(meter)
      .then((data) => { if (active) { setProducts(Array.isArray(data) ? data : []); setError(null); } })
      .catch(() => { if (active) setError('Não foi possível carregar os pacotes. Tente novamente.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [meter]);

  // Cria a order do pacote no Asaas (D-1). `document` só na 1ª compra sem CPF salvo.
  const startPurchase = useCallback((product, document) => {
    const input = { kind: 'pack', productId: product.id, methods: ['Pix'], providerSlug: 'asaas' };
    if (document) input.document = document;
    return billingService.createOrder(input);
  }, []);

  const handleBuy = useCallback(async (product) => {
    setBuyingId(product.id);
    try {
      const order = await startPurchase(product);
      setCheckoutOrder(order);
    } catch (err) {
      if (err?.response?.status === 400) {
        // backend exige CPF (D-6) → coleta no modal e refaz
        setCpf('');
        setCpfError(null);
        setCpfFor(product);
      } else {
        showError('Não foi possível iniciar o pagamento. Tente novamente.');
      }
    } finally {
      setBuyingId(null);
    }
  }, [startPurchase, showError]);

  const handleCpfSubmit = useCallback(async () => {
    const digits = cpf.replace(/\D/g, '');
    if (digits.length !== 11) { setCpfError('Digite um CPF válido (11 dígitos).'); return; }
    setCpfSubmitting(true);
    setCpfError(null);
    try {
      const order = await startPurchase(cpfFor, digits);
      setCpfFor(null);
      setCheckoutOrder(order);
    } catch (err) {
      setCpfError(err?.response?.data?.message || 'Não foi possível iniciar o pagamento. Verifique o CPF e tente novamente.');
    } finally {
      setCpfSubmitting(false);
    }
  }, [cpf, cpfFor, startPurchase]);

  const handleCheckoutClose = useCallback(() => setCheckoutOrder(null), []);

  const buyDisabled = !!buyingId || !!checkoutOrder;

  return (
    <StandardCard sx={{ mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 0.5 }}>Pacotes de mensagens</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Recarregue seu saldo de mensagens WhatsApp. Pagamento via PIX, na hora.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
      ) : products.length === 0 && !error ? (
        <Typography variant="body2" color="text.secondary">Nenhum pacote disponível no momento.</Typography>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {products.map((p) => (
            <Box
              key={p.id}
              sx={{
                flex: '1 1 200px', minWidth: 200, border: '1px solid', borderColor: 'divider',
                borderRadius: 2, p: 2, display: 'flex', flexDirection: 'column', gap: 0.5,
              }}
            >
              <Typography variant="subtitle1" fontWeight={600}>{p.name}</Typography>
              <Typography variant="body2" color="text.secondary">{p.creditQty} mensagens</Typography>
              <Typography variant="caption" color="text.secondary">{formatValidity(p.validityDays)}</Typography>
              <Typography variant="h6" sx={{ mt: 1 }}>{formatBRL(p.priceCents)}</Typography>
              <Button
                variant="contained" color="success" fullWidth sx={{ mt: 'auto' }}
                onClick={() => handleBuy(p)}
                disabled={buyDisabled}
                startIcon={buyingId === p.id ? <CircularProgress size={16} color="inherit" /> : null}
              >
                Comprar via PIX
              </Button>
            </Box>
          ))}
        </Box>
      )}

      {/* Modal de CPF (1º uso — D-6) */}
      <Dialog open={!!cpfFor} onClose={() => { if (!cpfSubmitting) setCpfFor(null); }} maxWidth="xs" fullWidth>
        <DialogTitle>Digite seu CPF para gerar o QR Code</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            O CPF é exigência do Banco Central para pagamentos via PIX. Guardamos com segurança e
            reutilizamos nas próximas compras.
          </Typography>
          <TextField
            label="CPF" value={cpf} onChange={(e) => setCpf(e.target.value)}
            fullWidth autoFocus inputProps={{ inputMode: 'numeric', maxLength: 14 }}
            error={!!cpfError} helperText={cpfError || 'Somente números'}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCpfFor(null)} disabled={cpfSubmitting}>Cancelar</Button>
          <Button
            variant="contained" onClick={handleCpfSubmit} disabled={cpfSubmitting}
            startIcon={cpfSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            Continuar
          </Button>
        </DialogActions>
      </Dialog>

      {checkoutOrder && (
        <PixQrCheckout order={checkoutOrder} onClose={handleCheckoutClose} />
      )}

      <AppSnackbar snackbar={snackbar} onClose={hideSnackbar} />
    </StandardCard>
  );
}
